import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ChevronDown, ClipboardCheck, Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { adminApi, formatApiError } from "@/context/AuthContext";
import { LIEUX } from "@/components/site/nav";

const Row = ({ place, onDone }) => {
  const [cats, setCats] = useState(place.categories?.length ? place.categories : [place.category]);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setCats((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));

  const validate = async () => {
    setBusy(true);
    try {
      await adminApi.patch(`/admin/places/${place.id}/review`, { categories: cats });
      onDone(place.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <li className="flex flex-wrap items-center gap-3 py-3 border-b border-white/10" data-testid={`review-row-${place.id}`}>
      <div className="w-full sm:w-56 min-w-0">
        <p className="text-white font-semibold truncate">{place.name}</p>
        <p className="text-zinc-400 text-xs truncate">{place.city} · {place.description}</p>
      </div>
      <div className="flex flex-wrap gap-1.5 flex-1">
        {LIEUX.map((l) => {
          const on = cats.includes(l.key);
          return (
            <button key={l.key} type="button" onClick={() => toggle(l.key)} aria-pressed={on} data-testid={`review-cat-${place.id}-${l.key}`}
              className={`px-2.5 h-9 text-[11px] uppercase font-bold tracking-[0.1em] border transition-colors ${on ? "bg-white text-black border-white" : "border-white/25 text-zinc-200 hover:border-white"}`}>
              {l.label}
            </button>
          );
        })}
      </div>
      <button type="button" onClick={validate} disabled={busy || !cats.length} data-testid={`review-validate-${place.id}`}
        className="inline-flex items-center gap-2 bg-primary text-white uppercase text-xs font-bold tracking-[0.15em] px-4 h-9 hover:bg-white hover:text-black transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Valider
      </button>
    </li>
  );
};

// « À vérifier » : places created from the agenda whose categories nobody confirmed yet
export const PlaceReview = () => {
  const qc = useQueryClient();
  const { data } = useApi("/places");
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState([]);
  const todo = (Array.isArray(data) ? data : []).filter((p) => p.from_agenda && !p.reviewed && !done.includes(p.id));
  if (!todo.length) return null;

  const onDone = (id) => {
    setDone((d) => [...d, id]);
    qc.invalidateQueries({ queryKey: ["/places"] });
  };

  return (
    <section className="border border-primary/60 bg-primary/10 p-4 mb-8" data-testid="place-review">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="place-review-toggle"
        className="w-full flex items-center gap-3 text-left">
        <ClipboardCheck className="w-5 h-5 text-primary" />
        <span className="font-display uppercase text-xl text-white flex-1">À vérifier · <span data-testid="place-review-count">{todo.length}</span> lieu{todo.length > 1 ? "x" : ""} de l'agenda</span>
        <ChevronDown className={`w-5 h-5 text-white transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <p className="text-zinc-300 text-sm mt-2">Coche les bonnes catégories (plusieurs possibles), puis « Valider » : le lieu sort de la liste.</p>
          <ul className="mt-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="place-review-list">
            {todo.map((p) => <Row key={p.id} place={p} onDone={onDone} />)}
          </ul>
        </>
      )}
    </section>
  );
};
